import React from 'react';
import { Link } from 'react-router-dom';
import LegalDocumentLayout, { LegalSection } from './LegalDocumentLayout';

const LAST_UPDATED = 'April 11, 2026';

/**
 * Terms aligned with RINK SaaS: scraping tools, user-generated jobs/results, PayPal subscriptions.
 */
export default function TermsOfServicePage() {
  return (
    <LegalDocumentLayout title="Terms of Service" lastUpdated={LAST_UPDATED}>
      <p className="text-gray-600 mb-8">
        These Terms of Service (“Terms”) govern your access to and use of the Rink Data platform and
        related services (“Services”) offered by RINK. By creating an account, using the dashboard or
        APIs, or purchasing a subscription, you agree to these Terms.
      </p>

      <LegalSection id="eligibility" title="1. Eligibility and accounts">
        <p>
          You must be of legal age to form a binding contract in your jurisdiction. You agree to provide
          accurate registration information and to keep your account credentials secure. You are
          responsible for all activity under your account.
        </p>
      </LegalSection>

      <LegalSection id="description" title="2. Description of the Services">
        <p>
          The Services allow you to configure and run web-scraping workflows, view job status, access
          and export result data, and manage subscription billing through our platform. Features may
          change over time; we may add, modify, or discontinue functionality with reasonable notice where
          appropriate.
        </p>
      </LegalSection>

      <LegalSection id="acceptable-use" title="3. Acceptable use">
        <p>You agree to use the Services only in compliance with applicable laws and regulations.</p>
        <p>
          <strong className="text-gray-900">Scraping and data.</strong> You are solely responsible for
          ensuring that your use of scraping tools, targets, and resulting data complies with applicable
          laws, third-party website terms, intellectual property rights, and privacy obligations. You must
          not use the Services to access systems without authorization, to violate robots.txt or similar
          restrictions where legally required, or to harvest data for unlawful discrimination, fraud, or
          harassment.
        </p>
        <p>
          <strong className="text-gray-900">Prohibited conduct.</strong> You may not probe, scan, or test
          the vulnerability of our systems without permission; interfere with other users; upload
          malware; or attempt to reverse engineer our software except as permitted by law.
        </p>
      </LegalSection>

      <LegalSection id="content" title="4. Your content and our intellectual property">
        <p>
          You retain rights to data you submit (such as search parameters and business data you upload).
          You grant us a limited license to host, process, and display that content solely to provide the
          Services to you.
        </p>
        <p>
          The platform, branding, documentation, and underlying software are owned by RINK or its
          licensors and are protected by intellectual property laws. Except for the limited rights
          expressly granted here, no rights are transferred to you.
        </p>
      </LegalSection>

      <LegalSection id="billing" title="5. Subscriptions and payments">
        <p>
          Paid plans are billed through PayPal (or other methods we disclose at checkout). By
          subscribing, you authorize recurring charges according to the plan and billing period you
          select, until you cancel in accordance with PayPal and our cancellation flows. Fees are
          non-refundable except where required by law or as explicitly stated at purchase.
        </p>
        <p>
          Taxes may apply based on your location. You are responsible for any taxes associated with your
          purchase other than our income taxes.
        </p>
      </LegalSection>

      <LegalSection id="disclaimer" title="6. Disclaimers">
        <p>
          THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT
          SCRAPED OR THIRD-PARTY DATA WILL BE ACCURATE, COMPLETE, OR LAWFUL FOR YOUR USE CASE.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="7. Limitation of liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, RINK AND ITS AFFILIATES, OFFICERS, AND EMPLOYEES WILL NOT
          BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR
          LOST PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICES. OUR AGGREGATE LIABILITY
          FOR CLAIMS RELATING TO THE SERVICES IS LIMITED TO THE GREATER OF THE AMOUNTS YOU PAID US FOR THE
          SERVICES IN THE TWELVE (12) MONTHS BEFORE THE CLAIM OR ONE HUNDRED U.S. DOLLARS (USD $100), WHICHEVER
          IS GREATER, EXCEPT WHERE PROHIBITED BY LAW.
        </p>
      </LegalSection>

      <LegalSection id="indemnity" title="8. Indemnity">
        <p>
          You will defend, indemnify, and hold harmless RINK from claims, damages, and expenses (including
          reasonable attorneys’ fees) arising from your use of the Services, your content or scraping
          activities, or your violation of these Terms or applicable law.
        </p>
      </LegalSection>

      <LegalSection id="termination" title="9. Suspension and termination">
        <p>
          We may suspend or terminate access to the Services for violation of these Terms, risk to the
          platform or other users, or as required by law. You may stop using the Services at any time.
          Provisions that by nature should survive (including intellectual property, disclaimers, liability
          limits, and indemnity) will survive termination.
        </p>
      </LegalSection>

      <LegalSection id="privacy" title="10. Privacy">
        <p>
          Our collection and use of personal information is described in our{' '}
          <Link to="/privacy" className="text-blue-600 hover:underline">
            Privacy Policy
          </Link>
          , which is incorporated into these Terms by reference.
        </p>
      </LegalSection>

      <LegalSection id="changes-terms" title="11. Changes to these Terms">
        <p>
          We may modify these Terms from time to time. We will post the updated Terms on this page and
          update the “Last updated” date. If changes are material, we may provide additional notice (for
          example by email or dashboard notice). Your continued use after the effective date constitutes
          acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection id="law" title="12. Governing law and venue">
        <p>
          These Terms are governed by the laws of the State of North Carolina and applicable federal law
          of the United States, without regard to conflict-of-law rules. You agree to the exclusive
          jurisdiction of the state and federal courts located in North Carolina for disputes arising
          from these Terms or the Services, subject to mandatory consumer protections in your jurisdiction
          where applicable.
        </p>
      </LegalSection>

      <LegalSection id="contact-terms" title="13. Contact">
        <ul className="list-none space-y-1">
          <li>
            Email:{' '}
            <a href="mailto:support@rinkdata.com" className="text-blue-600 hover:underline">
            support@rinkdata.com
            </a>{' '}
            or{' '}
            <a href="mailto:tom@cscbeyond.com" className="text-blue-600 hover:underline">
              tom@cscbeyond.com
            </a>
          </li>
          <li>Phone: (919) 679-1559</li>
          <li>Raleigh, NC, United States</li>
        </ul>
      </LegalSection>
    </LegalDocumentLayout>
  );
}
